import React, { Fragment, useMemo } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import get from "lodash/get";
import { useFormikContext } from "formik";
import Toggle from "react-toggle";
import {
  generateSelectOptions,
  getFieldPath,
  getToggleProps
} from "utils/form";
import { getValidationRequestDefault } from "utils/constants/validation/request";
import isUndefined from "lodash/isUndefined";
import { fetchIntegrations } from "actions/integration";
import { ensureIntegrations } from "utils/hooks";
import isNull from "lodash/isNull";
import MultiselectOption from "components/MultiselectOption";
import MultiSelect from "components/Form/fields/MultiSelect";
import { COMMAND_CONTENT_ID } from "components/CommandPalette/constants";
import PlayVideoTrigger from "components/PlayVideoTrigger";
import { STATE_KEYS } from "utils/constants/state";
import KeyOperatorValueList from "components/KeyOperatorValueList";
import {
  BUTTON_CLASS_CONTEXT,
  BUTTON_TAGS,
  EMPTY_SELECT_OPTION,
  SLIM_CTA_PROMPT_CLASSES
} from "utils/constants/ui";
import { getUUID } from "utils/uuid";
import Button from "components/Form/fields/Button";
import cloneDeep from "lodash/cloneDeep";
import ServiceActionOption from "../Connect/ServiceActionOption";
import {
  useAppCheckout,
  useCtxReferences,
  useIntegrationsForService
} from "utils/selectors";
import PATHS from "utils/paths";
import { interpolateRouteWithParams } from "utils/route";
import { SERVICES } from "utils/onboarding";
import SlimCTA from "components/SlimCTA";
import ActionRowLoader from "components/ActionRowLoader";
// import Overlay from "components/Overlay";
// import IntegrationBuilder from "components/FormerEditor/BlockSettings/FormBlock/CheckoutSettings/IntegrationBuilder";

const DEFAULT_LABEL = "Request";
const DEFAULT_DESCRIPTION = "Validate via external APIs.";

export const getConditionTemplate = () => ({
  uuid: getUUID(),
  key: "",
  operator: "",
  value: "",
  error: ""
});

const Request = ({
  name,
  actions,
  label = DEFAULT_LABEL,
  description = DEFAULT_DESCRIPTION
}) => {
  // const [showOverlay, setShowOverlay] = useState(null);

  const {
    values,
    touched,
    setFieldValue,
    setFieldTouched
  } = useFormikContext();

  const { application } = useCtxReferences();
  const integrationsCreatePath = interpolateRouteWithParams(
    PATHS.HUB_APPLICATIONS_INTEGRATIONS_CREATE,
    {
      application
    }
  );

  const { loading: loadingIntegrations } = ensureIntegrations({
    fetchIntegrations: actions.fetchIntegrations
  });
  const integrations = useIntegrationsForService(SERVICES.CUSTOM);

  const requestPath = getFieldPath(name, STATE_KEYS.FIELD.VALIDATION_REQUEST);
  const conditionsPath = getFieldPath(
    name,
    STATE_KEYS.FIELD.VALIDATION_REQUEST_VALIDATION_CONDITIONS
  );
  const conditions = get(values, conditionsPath);
  const requestConfig = get(values, requestPath);
  const hasVerificationConfig =
    !isUndefined(requestConfig) && !isNull(requestConfig);

  const requestEnabledPath = getFieldPath(
    name,
    STATE_KEYS.FIELD.VALIDATION_REQUEST_ENABLED
  );
  const requestEnableTouched = Boolean(get(requestEnabledPath, touched));

  const requestEnabled = Boolean(get(values, requestEnabledPath));
  const integrationIdPath = getFieldPath(
    name,
    STATE_KEYS.FIELD.VALIDATION_REQUEST_INTEGRATION_ID
  );
  const integrationId = get(values, integrationIdPath);
  const integrationIdTouched = Boolean(get(touched, integrationIdPath));
  const integrationOptions = useMemo(
    () =>
      integrations.map(({ id, name, description }) => ({
        id,
        label: name,
        description: description
      })),
    [integrations]
  );

  const selectedIntegration = integrationOptions.find(
    (option) => option.id === integrationId
  );

  let integrationSelections = [];
  if (selectedIntegration) {
    integrationSelections = [
      {
        id: selectedIntegration.id,
        name: selectedIntegration.label
      }
    ];
  }

  const integrationSelectProps = {
    value: integrationSelections,
    disabled: !requestEnabled,
    placeholder: "Select integration",
    options: integrationOptions,
    onChange: () => ({ value }) => {
      setFieldValue(integrationIdPath, value);

      if (!integrationIdTouched) {
        setFieldTouched(integrationIdPath, true);
      }
    },
    isMulti: false,
    labelTransform: (value) => value,
    components: {
      Option: MultiselectOption
    }
  };
  const checkout = useAppCheckout();
  const actionOptions =
    get(checkout, STATE_KEYS.APP_CHECKOUT.VALIDATION_ACTION_OPTIONS) || [];

  const requestActionsPath = getFieldPath(
    name,
    STATE_KEYS.FIELD.VALIDATION_REQUEST_ACTIONS
  );
  const requestActions = get(values, requestActionsPath);
  const actionsTouched = get(touched, requestActionsPath);
  const selectedDestinations = actionOptions.filter(
    (option) => requestActions && requestActions[option.uuid]
  );

  let actionsSelections = [];
  if (selectedDestinations.length > 0) {
    actionsSelections = generateSelectOptions(selectedDestinations);
  }

  const actionsSelectProps = {
    value: requestEnabled ? actionsSelections : [],
    placeholder: requestEnabled ? "Select action" : "Not enabled",
    disabled: !requestEnabled,
    options: [EMPTY_SELECT_OPTION, ...generateSelectOptions(actionOptions)],
    onChange: () => (selectedDestination) => {
      const updatedValue =
        selectedDestination && selectedDestination.uuid
          ? { [selectedDestination.uuid]: true }
          : null;
      setFieldValue(requestActionsPath, updatedValue);

      if (!actionsTouched) {
        setFieldTouched(requestActionsPath, true);
      }
    },
    isMulti: false,
    labelTransform: (value) => value,
    components: {
      Option: ServiceActionOption
    }
  };

  /**
   * Overlay onClose
   * Set the config from the overlay form into the main request field
   */
  // const setResult = (requestResult) => {
  //   setFieldValue(requestPath, requestResult);
  //   const isTouched = get(touched, requestPath);
  //   if (!isTouched) {
  //     setFieldTouched(requestPath, true);
  //   }

  //   setShowOverlay(false);
  // };
  // const overlayContent = (
  //   <Fragment>
  //     <IntegrationBuilder
  //       name={name}
  //       goBack={setResult}
  //       hideBuilder={() => setShowOverlay(false)}
  //     />
  //   </Fragment>
  // );

  let requestContent = null;
  if (requestEnabled) {
    if (loadingIntegrations) {
      requestContent = (
        <div className="pt3">
          <ActionRowLoader />
        </div>
      );
    } else {
      requestContent = (
        <Fragment>
          {integrations.length === 0 ? (
            <div className="pt3">
              <label className="f6 dib">Integration action</label>
              <label className="f7 dib pt1 pb3">
                {`Pass the field's value to your integration to check if it's invalid.`}
              </label>
              <SlimCTA
                subheader="No integrations created yet."
                customClasses={SLIM_CTA_PROMPT_CLASSES}
                actions={[
                  {
                    copy: "Create",
                    tag: BUTTON_TAGS.LINK,
                    href: integrationsCreatePath,
                    target: "_blank"
                  }
                ]}
              />
            </div>
          ) : (
            <div className="pt3">
              <label className="f6 dib">Integration action</label>
              <label className="f7 dib pt1 pb3">
                {`Pass the field's value to your integration to check if it's invalid.`}
              </label>
              <div className="ba br2 hairline-1 pa3">
                <div className="flex flex-row w-100 justify-between items-center">
                  <label className="f6 dib fw5">Integration</label>

                  <Button
                    theme="slim"
                    tag={BUTTON_TAGS.LINK}
                    target="_blank"
                    classes={{
                      contextClass: BUTTON_CLASS_CONTEXT.SECONDARY
                    }}
                    href={integrationsCreatePath}
                    copy="Add"
                  />
                </div>
                {/* TODO: If no integrations then link to integration create path  */}
                <div className="pt3">
                  <MultiSelect {...integrationSelectProps} />
                </div>
                <div className="pt3">
                  <div className="flex flex-row w-100 justify-between items-center">
                    <label className="f6 dib fw5">Conditions</label>
                    <Button
                      theme="slim"
                      classes={{
                        contextClass: BUTTON_CLASS_CONTEXT.SECONDARY
                      }}
                      copy="Add"
                      onClick={() => {
                        const updatedValues = conditions
                          ? cloneDeep(conditions)
                          : {};
                        updatedValues.push(getConditionTemplate());

                        setFieldValue(conditionsPath, updatedValues);
                        !get(touched, conditionsPath) &&
                          setFieldTouched(conditionsPath, true);
                      }}
                    />
                  </div>
                  <label className="f7 dib pt1 pb3 lh-copy">
                    {`Apply conditions to the integration response which determines whether it's valid.`}
                  </label>
                  <KeyOperatorValueList name={conditionsPath} />
                </div>
              </div>
            </div>
          )}
          <div className="pt3 mt3 bt hairline-1">
            <label className="f6 dib">Service check</label>
            <label className="f7 dib pt1">
              {`Pass the field's value to a 3rd party service to check if it's valid.`}
            </label>
            <div className="pt2 z-2 relative">
              <MultiSelect {...actionsSelectProps} />
            </div>
          </div>
        </Fragment>
      );
    }
  }

  return (
    <div className="w-100 flex flex-column">
      <div className="w-100 flex flex-row justify-between items-center">
        <label className="f6 dib fw6">{label}</label>
        <div className="flex flex-row">
          <PlayVideoTrigger id={COMMAND_CONTENT_ID.FIELDS.VALIDATION_REQUEST} />
          <div className="dib bl hairline-1 pl2 ml2">
            <Toggle
              {...getToggleProps({
                active: requestEnabled,
                setFieldValue: (_, active) => {
                  setFieldValue(requestEnabledPath, active);
                  const needsDefaultConfig = active && !hasVerificationConfig;
                  if (needsDefaultConfig) {
                    setFieldValue(requestPath, getValidationRequestDefault());
                  }

                  if (!requestEnableTouched) {
                    setFieldTouched(requestEnabledPath, true);
                  }
                }
              })}
            />
          </div>
        </div>
      </div>
      <div className="f6 pt2">{description}</div>
      {requestContent}
      {/* {showOverlay && requestEnabled && (
        <Overlay hideOverlay={() => setShowOverlay(false)}>
          {overlayContent}
        </Overlay>
      )} */}
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      fetchIntegrations
    },
    dispatch
  )
});

export default connect(null, mapDispatchToProps)(Request);
